import React, { useState, useEffect } from 'react';
import { employeeWalletAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Gift, Loader2, RefreshCw, Wallet, ShoppingBag,
  ArrowDown, ArrowUp, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { key: 'food', label: 'Food & Dining', icon: '🍽️', color: 'bg-orange-50 text-orange-600 border-orange-200' },
  { key: 'transport', label: 'Transport', icon: '🚌', color: 'bg-blue-50 text-blue-600 border-blue-200' },
  { key: 'housing', label: 'Housing', icon: '🏠', color: 'bg-purple-50 text-purple-600 border-purple-200' },
  { key: 'health', label: 'Health', icon: '💊', color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  { key: 'education', label: 'Education', icon: '📚', color: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
  { key: 'utilities', label: 'Utilities', icon: '⚡', color: 'bg-yellow-50 text-yellow-600 border-yellow-200' },
  { key: 'entertainment', label: 'Entertainment', icon: '🎬', color: 'bg-pink-50 text-pink-600 border-pink-200' },
  { key: 'other', label: 'Other', icon: '📦', color: 'bg-gray-50 text-gray-600 border-gray-200' },
];

export default function EmployeeFlexBenefits() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [spending, setSpending] = useState(false);
  const [selectedCat, setSelectedCat] = useState(null);
  const [spendAmount, setSpendAmount] = useState('');
  const [spendDesc, setSpendDesc] = useState('');
  const [spendVendor, setSpendVendor] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const d = await employeeWalletAPI.getFlexBenefits();
      setData(d);
    } catch { toast.error('Failed to load flex benefits'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSpend = async () => {
    const amt = parseFloat(spendAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (!selectedCat) { toast.error('Select a category'); return; }
    setSpending(true);
    try {
      const res = await employeeWalletAPI.spendFlexBenefit({
        category: selectedCat,
        amount: amt,
        description: spendDesc || `${selectedCat} expense`,
        vendor_name: spendVendor,
      });
      toast.success(`Spent NGN ${amt.toLocaleString()} on ${selectedCat}. New balance: NGN ${res.new_balance.toLocaleString()}`);
      setSpendAmount(''); setSpendDesc(''); setSpendVendor(''); setSelectedCat(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Spending failed');
    } finally { setSpending(false); }
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <Loader2 className="w-8 h-8 animate-spin text-pink-400" />
    </div>
  );

  if (!data?.has_flex_benefit) return (
    <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="no-flex-benefit">
      <Gift className="w-12 h-12 text-gray-300 mb-3" />
      <h2 className="text-lg font-semibold text-gray-700">No Flex Benefit</h2>
      <p className="text-sm text-gray-500 mt-1">Your company hasn't set up a flex benefit for you yet.</p>
    </div>
  );

  const fmt = (n) => Number(n || 0).toLocaleString('en-NG', { style: 'currency', currency: 'NGN' });
  const spending_data = data.spending_by_category || {};
  const allowedCats = CATEGORIES.filter(c => (data.allowed_categories || []).includes(c.key));

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="employee-flex-benefits">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Flex Benefits</h1>
          <p className="text-sm text-gray-500">Spend across any category you choose</p>
        </div>
        <Button variant="ghost" size="sm" onClick={load} data-testid="refresh-flex">
          <RefreshCw className="w-3.5 h-3.5" />
        </Button>
      </div>

      {/* Balance Card */}
      <Card className="border-2 border-pink-200 bg-gradient-to-br from-pink-50 to-white overflow-hidden">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-pink-400 font-medium uppercase tracking-wider">Available Balance</p>
              <p className="text-3xl font-extrabold text-gray-900 mt-1" data-testid="flex-balance">{fmt(data.balance)}</p>
              <p className="text-xs text-gray-500 mt-1">Monthly allocation: {fmt(data.monthly_amount)}</p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-pink-100 flex items-center justify-center">
              <Wallet className="w-7 h-7 text-pink-500" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Spend Section */}
      <Card className="border-gray-200">
        <CardContent className="p-5 space-y-4">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <ShoppingBag className="w-4 h-4 text-gray-400" /> Spend Benefits
          </h3>

          {/* Category Grid */}
          <div className="grid grid-cols-4 gap-2">
            {allowedCats.map(c => (
              <button
                key={c.key}
                onClick={() => setSelectedCat(selectedCat === c.key ? null : c.key)}
                data-testid={`category-${c.key}`}
                className={`p-3 rounded-xl border text-center transition-all ${
                  selectedCat === c.key
                    ? 'ring-2 ring-pink-400 border-pink-300 bg-pink-50 scale-105'
                    : `${c.color} hover:scale-102`
                }`}
              >
                <span className="text-xl block mb-1">{c.icon}</span>
                <span className="text-[10px] font-medium block leading-tight">{c.label}</span>
                {spending_data[c.key] && (
                  <span className="text-[9px] text-gray-400 block mt-0.5">Spent: {fmt(spending_data[c.key])}</span>
                )}
              </button>
            ))}
          </div>

          {selectedCat && (
            <div className="space-y-3 pt-2 border-t border-gray-100 animate-in fade-in slide-in-from-top-2">
              <div>
                <Label className="text-xs">Amount (NGN)</Label>
                <Input
                  type="number" value={spendAmount}
                  onChange={e => setSpendAmount(e.target.value)}
                  placeholder="5000" className="mt-1"
                  data-testid="spend-amount"
                />
              </div>
              <div>
                <Label className="text-xs">Description (optional)</Label>
                <Input
                  value={spendDesc} onChange={e => setSpendDesc(e.target.value)}
                  placeholder="Lunch at restaurant" className="mt-1"
                  data-testid="spend-description"
                />
              </div>
              <div>
                <Label className="text-xs">Vendor / Merchant (optional)</Label>
                <Input
                  value={spendVendor} onChange={e => setSpendVendor(e.target.value)}
                  placeholder="Restaurant name" className="mt-1"
                  data-testid="spend-vendor"
                />
              </div>
              <Button
                onClick={handleSpend} disabled={spending}
                className="w-full bg-pink-500 hover:bg-pink-600 text-white"
                data-testid="spend-submit-btn"
              >
                {spending ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>Spend {spendAmount ? fmt(parseFloat(spendAmount)) : ''} on {selectedCat}</>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transaction History */}
      {data.recent_transactions?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Recent Activity</h3>
          <div className="space-y-1.5">
            {data.recent_transactions.map(t => {
              const cat = CATEGORIES.find(c => c.key === t.category);
              return (
                <div key={t.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-white border border-gray-100" data-testid={`flex-txn-${t.id}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${t.type === 'fund' ? 'bg-emerald-50' : 'bg-gray-50'}`}>
                    {t.type === 'fund' ? (
                      <ArrowDown className="w-3.5 h-3.5 text-emerald-500" />
                    ) : (
                      <span className="text-sm">{cat?.icon || '💰'}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {t.type === 'fund' ? 'Monthly Funding' : t.description || t.category}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {t.vendor_name && `${t.vendor_name} · `}
                      {new Date(t.created_at).toLocaleDateString('en-NG', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  <span className={`text-sm font-semibold ${t.type === 'fund' ? 'text-emerald-600' : 'text-gray-900'}`}>
                    {t.type === 'fund' ? '+' : '-'}{fmt(t.amount)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
